import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { ref, push, set } from 'firebase/database';

type BotType = 'whatsapp' | 'website' | 'voice' | 'facebook';

interface SaveAsTemplateProps {
  botType: BotType;
  triggers: any[];
  // Optional extra fields per bot type
  greeting?: string;
  offlineMessage?: string;
  language?: string;
}

const S = {
  input: { width: '100%', padding: '10px 13px', background: '#0f1117', border: '1px solid #1e2330', borderRadius: '9px', color: '#e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' },
  label: { display: 'block', fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '7px' },
};

export default function SaveAsTemplate({ botType, triggers, greeting, offlineMessage, language }: SaveAsTemplateProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('🤖');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!auth.currentUser) { alert('You must be logged in'); return; }
    if (!name.trim()) { alert('Enter a template name'); return; }
    setSaving(true);
    try {
      const newRef = push(ref(db, 'templates'));
      await set(newRef, {
        id: newRef.key,
        name: name.trim(),
        description: description.trim(),
        icon: icon.trim() || '🤖',
        botType,
        industry: 'custom',
        triggers,
        greeting: greeting || '',
        offlineMessage: offlineMessage || '',
        language: language || '',
        isPublic: false,           // private — only visible to this user
        createdBy: auth.currentUser.uid,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setOpen(false);
        setName('');
        setDescription('');
        setIcon('🤖');
      }, 1800);
    } catch (err) {
      alert('Error saving template: ' + err);
    } finally {
      setSaving(false);
    }
  };

  const COLOR: Record<BotType, string> = {
    whatsapp: '#10b981',
    website: '#3b82f6',
    voice: '#8b5cf6',
    facebook: '#1877f2',
  };
  const color = COLOR[botType];

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '7px 14px', background: '#1e2330',
          color: '#94a3b8', border: '1px solid #1e2330',
          borderRadius: '8px', cursor: 'pointer',
          fontSize: '12px', fontWeight: '500',
        }}
      >
        💾 Save as template
      </button>

      {/* Modal */}
      {open && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 998 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: 'fixed', inset: 0, zIndex: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{
              background: '#15181f', borderRadius: '16px',
              border: '1px solid #1e2330', padding: '28px',
              maxWidth: '420px', width: '90%',
              pointerEvents: 'all',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}>
              {saved ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                  <div style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '16px' }}>Template saved!</div>
                  <div style={{ color: '#475569', fontSize: '13px', marginTop: '6px' }}>
                    Find it in your Template Library under "My Templates"
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                      💾 Save as template
                    </h2>
                    <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '18px' }}>✕</button>
                  </div>

                  {/* Bot type badge */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: color + '15', border: `1px solid ${color}30`, color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', marginBottom: '18px' }}>
                    {botType} bot · {triggers.length} triggers
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={S.label}>Template name *</label>
                    <input
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="e.g. My Barber Shop Flow"
                      style={S.input}
                      autoFocus
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={S.label}>Description</label>
                    <input
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="What is this template for?"
                      style={S.input}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={S.label}>Icon</label>
                    <input
                      value={icon}
                      onChange={e => setIcon(e.target.value)}
                      placeholder="🤖"
                      style={{ ...S.input, width: '80px' }}
                    />
                  </div>

                  <div style={{ background: '#10b98110', border: '1px solid #10b98130', borderRadius: '8px', padding: '10px 14px', marginBottom: '20px', fontSize: '12px', color: '#10b981' }}>
                    🔒 This template is private — only visible to you
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setOpen(false)} style={{ padding: '9px 18px', background: '#1e2330', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || !name.trim()}
                      style={{ padding: '9px 22px', background: saving || !name.trim() ? '#1e2330' : '#5B4FE8', color: saving || !name.trim() ? '#475569' : 'white', border: 'none', borderRadius: '8px', cursor: saving || !name.trim() ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      {saving ? 'Saving...' : '💾 Save template'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
